/**
 * Сетка референсов Pinterest в двух видах:
 * - `masonry` — плитка: пин целиком, разной высоты. Раскладка своя, а не CSS
 *   columns: те льют пины сверху вниз по колонке, и в ленте по тренду №2
 *   оказывался под №1, а не рядом. Здесь каждый следующий пин идёт в самую
 *   короткую колонку — порядок читается по рядам, как у Pinterest;
 * - `rows` — ровные ряды одинаковых плиток 3:4 (кадр обрезан): глаз идёт по
 *   строке, а не по ломаной, и сотня пинов просматривается быстрее.
 *
 * Сетка берёт превью 474px (`thumb_path`), оригинал — только если превью нет:
 * пятьсот оригиналов по 360 КБ положили бы вкладку.
 *
 * Видео-пин проигрывается при наведении, без звука: `<video>` появляется
 * только на время наведения, а до того видна обложка — обычный `<img>`.
 * Шестьдесят mp4 разом положили бы и сеть, и вкладку. Ролик не скачан
 * (`video_path` пуст) — обложка со значком, а не чёрный прямоугольник.
 *
 * Шлюз домена режет пачку запросов (`limit_req` nginx → 429, 03.10.26): сотня
 * ленивых превью при прокрутке упиралась в лимит, и часть плиток оставалась
 * пустой. Поэтому превью при ошибке переспрашивается с нарастающей паузой.
 *
 * Выделение (03.10.26): клик по картинке — просмотр, Ctrl/Cmd-клик — выделить,
 * Shift-клик — диапазон, в режиме «выделять» обычный клик = выделить. Чекбокс —
 * ОТДЕЛЬНАЯ кнопка-сосед в правом верхнем углу, а не внутри кнопки картинки:
 * кнопка в кнопке невалидна. Сам набор выделенных держит владелец
 * (`References`), сетка только сообщает индекс в показанном порядке.
 *
 * Теги — второй строкой подписи, а не поверх картинки: там уже сохранения,
 * тренд и библиотека.
 */
import { Bookmark, Check, ImageOff, Library, Play, SquareArrowOutUpRight, TrendingUp } from "lucide-react";
import { useState } from "react";

import { Link } from "react-router";

import { mediaUrl, num, thumbUrl } from "@/lib/media";
import { tagStyle } from "@/lib/tags";
import { ageText, trendText, type Pin } from "@/lib/trends";
import { Masonry } from "../../shared/grid/Masonry";
import { RetryImg } from "../../shared/grid/RetryImg";

export { Masonry } from "../../shared/grid/Masonry";
export { RetryImg } from "../../shared/grid/RetryImg";

export type GridLayout = "masonry" | "rows";

/** Тренд от двух медиан — выделяем: это и есть аномалия внутри среза. */
const HOT_TREND = 2;
/** Подпись под плиткой — в долях ширины, чтобы колонки выравнивались честнее. */
const CAPTION_SHARE = 0.12;
/** Вторая строка подписи — фишки тегов. */
const TAGS_SHARE = 0.11;
/** Фишек тегов на плитке; остальные — «+N». */
const TILE_TAGS = 3;

/** Выделение и подписи — общие для всех плиток сетки. */
export type GridShared = {
  selected: ReadonlySet<number>;
  selecting: boolean;
  /** Индекс — в показанном порядке; `range` — Shift: диапазон от прошлого выделенного. */
  onToggle: (index: number, range: boolean) => void;
  tagNames: ReadonlyMap<number, string>;
  /** Режим подборки: подпись «из каких слов» найден пин. */
  wordNames?: ReadonlyMap<number, string>;
  /** Чья медиана у тренда: «типа» | «подборки» — в подсказке. */
  medianOf: string;
};

/** Высота плитки в ширинах колонки: картинка по пропорциям + подпись. */
function pinHeight(pin: Pin): number {
  const ratio = pin.asset_width && pin.asset_height ? pin.asset_height / pin.asset_width : 4 / 3;
  return ratio + CAPTION_SHARE + (pin.tag_ids.length ? TAGS_SHARE : 0);
}

export function PinGrid({
  pins,
  layout,
  onOpen,
  ...shared
}: {
  pins: Pin[];
  layout: GridLayout;
  onOpen: (index: number) => void;
} & GridShared) {
  if (layout === "rows")
    return (
      <div className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-3" data-annot="сетка референсов">
        {pins.map((pin, i) => (
          <PinTile key={pin.id} pin={pin} index={i} layout={layout} onOpen={() => onOpen(i)} {...shared} />
        ))}
      </div>
    );
  return (
    <Masonry
      items={pins}
      keyOf={(pin) => pin.id}
      heightOf={pinHeight}
      renderTile={(pin, index) => (
        <PinTile pin={pin} index={index} layout="masonry" onOpen={() => onOpen(index)} {...shared} />
      )}
    />
  );
}

function PinTile({
  pin,
  index,
  layout,
  onOpen,
  selected,
  selecting,
  onToggle,
  tagNames,
  wordNames,
  medianOf,
}: { pin: Pin; index: number; layout: GridLayout; onOpen: () => void } & GridShared) {
  const [hover, setHover] = useState(false);
  const isSelected = selected.has(pin.id);
  const boxVisible = isSelected || selecting || selected.size > 0;
  const words = wordNames ? (pin.type_ids ?? []).map((id) => wordNames.get(id) ?? `#${id}`) : [];
  const picture = pin.thumb_path ?? pin.asset_path;
  const ratio =
    layout === "rows"
      ? "3 / 4"
      : pin.asset_width && pin.asset_height
        ? `${pin.asset_width} / ${pin.asset_height}`
        : "3 / 4";
  const isVideo = pin.kind === "video";
  const playable = isVideo && pin.video_path !== null;
  const play = () => setHover(true);
  const stop = () => setHover(false);

  return (
    <figure className="min-w-0">
      <div className="group relative">
        <button
          type="button"
          onClick={(e) => (selecting || e.ctrlKey || e.metaKey || e.shiftKey ? onToggle(index, e.shiftKey) : onOpen())}
          // Shift-клик иначе выделяет текст страницы от прошлого клика до этого.
          onMouseDown={(e) => (e.shiftKey ? e.preventDefault() : undefined)}
          onMouseEnter={playable ? play : undefined}
          onMouseLeave={playable ? stop : undefined}
          onFocus={playable ? play : undefined}
          onBlur={playable ? stop : undefined}
          className={`relative block w-full overflow-hidden rounded-[var(--radius-card)] border border-border bg-bg-subtle text-left focus-visible:outline-2 focus-visible:outline-accent ${
            isSelected ? "outline-2 outline-offset-[-2px] outline-accent" : ""
          }`}
          style={{ aspectRatio: ratio }}
          title={pin.title ?? undefined}
        >
          {!picture ? (
            <span className="absolute inset-0 grid place-items-center text-[12px] text-fg-faint">
              <span className="grid justify-items-center gap-1">
                <ImageOff size={18} />
                картинка не скачана
              </span>
            </span>
          ) : (
            <>
              <RetryImg
                src={thumbUrl(picture)}
                alt={pin.title ?? ""}
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
              />
              {playable && hover ? (
                // Поверх обложки, пока мышь на плитке; ушла — ролик выгружается.
                <video
                  src={mediaUrl(pin.video_path)}
                  muted
                  loop
                  playsInline
                  autoPlay
                  className="absolute inset-0 h-full w-full object-cover"
                />
              ) : null}
            </>
          )}

          <span className="absolute top-1.5 left-1.5 grid justify-items-start gap-1">
            <span
              className="inline-flex items-center gap-1 rounded-[4px] bg-black/60 px-1.5 py-0.5 text-[11px] font-medium text-white tnum"
              title="Сохранения картинки (все её копии)"
            >
              <Bookmark size={10} />
              {num(pin.saves)}
            </span>
            {pin.trend !== null ? (
              <span
                className={`inline-flex items-center gap-1 rounded-[4px] px-1.5 py-0.5 text-[11px] font-medium tnum ${
                  pin.trend >= HOT_TREND ? "bg-accent text-bg" : "bg-black/60 text-white"
                }`}
                title={`Репинов этой копии в день: ${pin.speed ?? "—"} — во столько раз быстрее медианы ${medianOf} (${
                  isVideo ? "среди видео" : "среди картинок"
                })`}
              >
                <TrendingUp size={10} />
                {trendText(pin.trend)}
              </span>
            ) : null}
          </span>
          {isVideo ? (
            <span
              className={`absolute right-1.5 bottom-1.5 grid size-6 place-items-center rounded-full text-white ${
                playable ? "bg-black/60" : "bg-black/35"
              }`}
              title={playable ? "Видео: наведите, чтобы проиграть" : "Видео: ролик не скачан, только обложка"}
            >
              <Play size={11} fill="currentColor" />
            </span>
          ) : null}
          {pin.in_libraries > 0 ? (
            <span
              className="absolute bottom-1.5 left-1.5 inline-flex items-center gap-1 rounded-[4px] bg-accent px-1.5 py-0.5 text-[11px] font-medium text-bg"
              title="Уже лежит в библиотеках референсов"
            >
              <Library size={10} />в библиотеке ×{pin.in_libraries}
            </span>
          ) : null}
        </button>
        <button
          type="button"
          aria-pressed={isSelected}
          aria-label={isSelected ? "Снять выделение" : "Выделить"}
          title="Выделить (Shift — диапазон)"
          onMouseDown={(e) => (e.shiftKey ? e.preventDefault() : undefined)}
          onClick={(e) => {
            e.stopPropagation();
            onToggle(index, e.shiftKey);
          }}
          className={`absolute top-1.5 right-1.5 grid size-5 place-items-center rounded-[4px] border transition-opacity ${
            isSelected ? "border-accent bg-accent text-bg" : "border-white/80 bg-black/45 text-white hover:bg-black/65"
          } ${boxVisible ? "opacity-100" : "opacity-0 group-hover:opacity-100 focus-visible:opacity-100"}`}
        >
          {isSelected ? <Check size={13} strokeWidth={3} /> : null}
        </button>
      </div>
      <figcaption className="mt-1 grid min-w-0 gap-1 px-0.5 text-[11.5px] text-fg-faint">
        <span className="flex min-w-0 gap-1.5 overflow-hidden whitespace-nowrap">
          <span
            className="flex-none tnum"
            title={pin.origin === "repin" ? "Дата репина — картинка старше" : "Опубликован на Pinterest"}
          >
            {ageText(pin.age_days)}
            {pin.origin === "repin" ? " · репин" : ""}
          </span>
          {words.length ? (
            <span className="min-w-0 truncate text-fg-muted" title={`Найден по словам: ${words.join(", ")}`}>
              · {words[0]}
              {words.length > 1 ? ` +${words.length - 1}` : ""}
            </span>
          ) : null}
          {pin.domain ? (
            // Тесно — сжимается домен, а не слово: слово на экране подборки важнее.
            <span className="min-w-0 shrink-[100] truncate" title={pin.domain}>
              · {pin.domain}
            </span>
          ) : null}
          <Link
            to={`/pins/${pin.id}`}
            title="Карточка пина"
            aria-label="Карточка пина"
            onClick={(e) => e.stopPropagation()}
            className="ml-auto flex-none text-fg-faint hover:text-fg"
          >
            <SquareArrowOutUpRight size={11} />
          </Link>
        </span>
        {pin.tag_ids.length ? (
          <span className="flex min-w-0 flex-wrap gap-1" data-annot="теги плитки">
            {pin.tag_ids.slice(0, TILE_TAGS).map((id) => (
              <span
                key={id}
                className="max-w-full truncate rounded-[3px] px-1 text-[10.5px] leading-[16px]"
                style={tagStyle(id)}
              >
                {tagNames.get(id) ?? `#${id}`}
              </span>
            ))}
            {pin.tag_ids.length > TILE_TAGS ? (
              <span
                className="text-[10.5px] leading-[16px] text-fg-muted"
                title={pin.tag_ids.slice(TILE_TAGS).map((id) => tagNames.get(id) ?? `#${id}`).join(", ")}
              >
                +{pin.tag_ids.length - TILE_TAGS}
              </span>
            ) : null}
          </span>
        ) : null}
      </figcaption>
    </figure>
  );
}
